/**
 * CTA Icon System — Obsidian Pulse
 *
 * Action-specific Lucide marks inherit the CTA foreground colour so every button
 * keeps its own magenta, leaf-green, gold or neutral language. GSAP supplies the
 * motion; reduced-motion visitors keep a still icon.
 *
 * Two things about how it is triggered, both deliberate:
 *
 *   - The listener is on the button, not on the icon. Hovering a button and
 *     getting nothing until the pointer happens to cross the small mark is not a
 *     hover state, it is a hidden one.
 *   - The motion repeats for as long as the pointer stays. A single 200ms gesture
 *     fired on entry is over before the eye reaches it, which read as nothing
 *     happening at all.
 *
 * The icon is also the only thing that moves. Button hover styles carry no
 * transform, because lifting a button lifts its label, and the label is type.
 */
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type CtaIconProps = {
  icon: LucideIcon;
  size?: number;
  className?: string;
};

/**
 * Big enough for the motion to register.
 *
 * At 16px the animations were technically running and practically invisible.
 */
const DEFAULT_ICON_SIZE = 21;

export default function CtaIcon({ icon: Icon, size = DEFAULT_ICON_SIZE, className }: CtaIconProps) {
  const iconRef = useRef<HTMLSpanElement>(null);
  const loopRef = useRef<gsap.core.Timeline | null>(null);
  const iconName = ((Icon as unknown as { displayName?: string; name?: string }).displayName || (Icon as unknown as { name?: string }).name || "icon").toLowerCase();

  const canAnimate = () =>
    typeof window !== "undefined" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /**
   * The gesture for this icon, as a timeline that repeats until it is killed.
   *
   * Each branch is the same movement the single-shot version played, wrapped so
   * it can hold for a beat and go again while the pointer stays on the button.
   */
  const buildLoop = (root: HTMLElement, svg: SVGElement) => {
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.45, defaults: { overwrite: "auto" } });

    if (iconName.includes("mic")) {
      return tl
        .to(root, { y: -2, scale: 1.1, duration: 0.16, ease: "power3.out" })
        .to(svg, { rotate: -9, transformOrigin: "50% 50%", duration: 0.12, ease: "power2.inOut", repeat: 3, yoyo: true })
        .to(root, { y: 0, scale: 1, duration: 0.2, ease: "power3.out" });
    }

    if (iconName.includes("arrow") || iconName.includes("move")) {
      // Travels out, then returns from behind rather than snapping back.
      return tl
        .to(root, { x: 5, scale: 1.08, duration: 0.26, ease: "power3.out" })
        .to(root, { x: 0, scale: 1, duration: 0.3, ease: "power2.inOut" });
    }

    if (iconName.includes("calendar")) {
      return tl
        .to(root, { y: -3, rotate: -6, scale: 1.09, duration: 0.26, ease: "power3.out" })
        .to(root, { y: 0, rotate: 0, scale: 1, duration: 0.32, ease: "power2.inOut" });
    }

    if (iconName.includes("ticket")) {
      return tl
        .to(root, { y: -2, rotate: -5, scale: 1.08, duration: 0.24, ease: "power3.out" })
        .to(root, { y: 0, rotate: 0, scale: 1, duration: 0.3, ease: "power2.inOut" });
    }

    if (iconName.includes("check")) {
      return tl
        .to(root, { scale: 1.18, duration: 0.15, ease: "power3.out" })
        .to(root, { scale: 1, duration: 0.34, ease: "elastic.out(1, 0.5)" });
    }

    if (iconName.includes("user") || iconName.includes("handshake")) {
      return tl
        .to(root, { y: -3, scale: 1.09, duration: 0.24, ease: "back.out(1.8)" })
        .to(root, { y: 0, scale: 1, duration: 0.3, ease: "power2.inOut" });
    }

    return tl
      .to(root, { y: -2, scale: 1.09, duration: 0.22, ease: "power3.out" })
      .to(root, { y: 0, scale: 1, duration: 0.28, ease: "power2.inOut" });
  };

  /*
   * Bound to the enclosing button rather than to the icon.
   *
   * The icon is a few pixels wide inside a much larger target, so a listener on
   * the mark itself meant most hovers over the button produced nothing.
   */
  useEffect(() => {
    const root = iconRef.current;
    const svg = root?.querySelector("svg");
    if (!root || !svg) return;

    const trigger = root.closest("button, a") ?? root;

    const start = () => {
      if (!canAnimate()) return;
      loopRef.current?.kill();
      loopRef.current = buildLoop(root, svg);
    };

    const stop = () => {
      loopRef.current?.kill();
      loopRef.current = null;
      if (!canAnimate()) return;
      gsap.to([root, svg], { x: 0, y: 0, rotate: 0, scale: 1, duration: 0.24, ease: "power3.out", overwrite: "auto" });
    };

    trigger.addEventListener("pointerenter", start);
    trigger.addEventListener("pointerleave", stop);
    trigger.addEventListener("focusin", start);
    trigger.addEventListener("focusout", stop);

    return () => {
      trigger.removeEventListener("pointerenter", start);
      trigger.removeEventListener("pointerleave", stop);
      trigger.removeEventListener("focusin", start);
      trigger.removeEventListener("focusout", stop);
      loopRef.current?.kill();
      loopRef.current = null;
    };
  }, [iconName]);

  return (
    <span
      ref={iconRef}
      className={cn("chi-cta-icon", className)}
      data-icon-motion={iconName}
      aria-hidden="true"
    >
      <Icon size={size} strokeWidth={1.9} />
    </span>
  );
}
